import { Button, FormControl, FormLabel, Input, Typography } from '@mui/joy';
import { Stack } from '@mui/system';
import { useMutation, useQuery } from '@tanstack/react-query';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';

import { ContainerLayout } from '@/components/ContainerLayout';
import { ErrorAlert } from '@/components/errors/ErrorAlert';
import { getI18nRadioLabel } from '@/components/form-elements/I18nRadioLabel';
import { OrganizationAutocomplete } from '@/components/form-elements/OrganizationAutocomplete';
import { RadioWithDescriptionGroup } from '@/components/form-elements/RadioWithDescriptionGroup';
import { ButtonLink } from '@/components/router/ButtonLink';
import { I18N_DE } from '@/i18n/de';
import { CreateOrganizationAdminLevelDto, Organization, OrganizationAdminLevelPermission } from '@/modules/api';
import {
  backendApiAdminLevelsFindOneOptions,
  backendApiOrganizationsCreateOrganizationAdminLevelMutation,
} from '@/modules/api/@tanstack/react-query.gen';

export const Route = createFileRoute(
  '/_authenticated/_management/administration/verwaltungsebenen/$adminLevelId/organisationen/hinzufuegen',
)({
  // eslint-disable-next-line @typescript-eslint/no-use-before-define
  component: CreateOrganizationAdminLevelRoute,
});

type FormData = Pick<CreateOrganizationAdminLevelDto, 'permission'>;

function CreateOrganizationAdminLevelRoute() {
  const { adminLevelId } = Route.useParams();
  const navigate = useNavigate();

  const [organization, setOrganization] = useState<Organization>();

  const { data, error } = useQuery(backendApiAdminLevelsFindOneOptions({ path: { adminLevelId } }));

  const { mutate, isPending } = useMutation({
    ...backendApiOrganizationsCreateOrganizationAdminLevelMutation(),
    onSuccess: async () =>
      navigate({
        to: '/administration/organisationen/$organizationId/verwaltungsebenen/$adminLevelId',
        params: { organizationId: organization?.id!, adminLevelId },
      }),
  });

  const { control, handleSubmit } = useForm<FormData>({
    defaultValues: {
      permission: OrganizationAdminLevelPermission.EDIT,
    },
  });

  if (error) {
    return <ErrorAlert error={error} />;
  }

  return (
    data && (
      <ContainerLayout>
        <Stack
          component="form"
          gap={2}
          onSubmit={handleSubmit((formData) => {
            if (isPending) return;

            mutate({ path: { organizationId: organization?.id!, adminLevelId }, body: formData });
          })}
        >
          <Typography level="h3">Verwaltungsebene</Typography>

          <FormControl>
            <FormLabel>Name</FormLabel>
            <Input value={data?.name ?? ''} readOnly />
          </FormControl>

          <FormControl>
            <FormLabel>Ebene</FormLabel>
            <Input value={(data && I18N_DE.AdminLevelType[data.type]) ?? ''} readOnly />
          </FormControl>

          <FormControl>
            <FormLabel>Organisation</FormLabel>
            <OrganizationAutocomplete
              variant="outlined"
              onChange={(event, value) => {
                setOrganization(value || undefined);
              }}
              excludeIds={
                data?.organizations?.map((OrganizationAdminLevel) => OrganizationAdminLevel.organizationId) || []
              }
            />
          </FormControl>

          <FormControl>
            <FormLabel>Berechtigung</FormLabel>
            <Controller
              control={control}
              name="permission"
              render={({ field }) => (
                <RadioWithDescriptionGroup
                  {...field}
                  options={Object.values(OrganizationAdminLevelPermission)}
                  optionLabel={getI18nRadioLabel({
                    i18nTitle: I18N_DE.OrganizationAdminLevelPermission,
                    i18nDescription: I18N_DE.OrganizationAdminLevelPermissionDescription,
                  })}
                />
              )}
            />
          </FormControl>

          <Stack direction="row" gap={2} justifyContent="space-between" marginTop={4}>
            <ButtonLink
              variant="outlined"
              to="/administration/verwaltungsebenen/$adminLevelId"
              params={{ adminLevelId }}
            >
              Verwaltungsebene
            </ButtonLink>
            <Button type="submit" disabled={!organization || isPending} loading={isPending}>
              Hinzufügen
            </Button>
          </Stack>
        </Stack>
      </ContainerLayout>
    )
  );
}
